import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { ArrowBack, VisibilityOutlined } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useNavigation } from "../../../hooks/useNavigation";
import { tokens } from "../../../theme";
import Header from "../../common/Header";
import Button from "../../common/Button";
import Card from "../../common/Card";
import CustomTable from "../../common/CustomTable";
import { Column, setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { getRecentAiAuditDetailsByIdData, getAllAuditDetailsByIdData, resetAllAuditDetails, useAiAuditReducer, getAiAuditDetailByIdData } from "../../../redux/slice/AI/AiAuditSlice";
import { getComplianceColor } from "../../../utils/Utility";
import CustomIconButton from "../../common/CustomIconButton";
import MedicalNoteAuditPopup from "./MedicalNoteAuditPopup";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import CustomCommentPopup from "../../common/CustomCommentPopup";

interface AiAuditDetailsProps {}

interface AuditDetailRow {
  createdByUserName: string;
  compliancePercentage: number | string;
  createdAt: string;
  actionType: string;
  action: any;
}

const AiAuditDetails: React.FC<AiAuditDetailsProps> = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();

  const { allAuditDetails } = useAiAuditReducer();
  const [auditId, setAuditId] = useState<string | null>(null);
   const [showMedicalAuditPopup, setShowMedicalAuditPopup] = useState(false);
    const [selectedMedicalAuditData, setSelectedMedicalAuditData] = useState<any>(null);
    const [showCommentPopup, setShowCommentPopup] = useState(false);
    const [commentData, setCommentData] = useState({ title: "", body: "" });
  // Get auditId from location state
  useEffect(() => {
    if (location.state && location.state.auditId) {
      setAuditId(location.state.auditId);
    } else {
      // If no auditId, redirect back to list
      handleAlert("No audit ID provided", "error");
      navigate("/ai_medical_audit");
    }
  }, [location.state]);

  // Fetch audit details when auditId is available or popup state changes
  useEffect(() => {
    if (auditId) {
      fetchAuditDetails();
    }
  }, [auditId, showMedicalAuditPopup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAllAuditDetails());
    };
  }, []);

  const fetchAuditDetails = async () => {
    if (!auditId) return;

    try {
      const response = await dispatch(getAllAuditDetailsByIdData(auditId));
      if (response.meta.requestStatus !== "fulfilled") {
        handleAlert("Failed to load audit details", "error");
      }
    } catch (error) {
      console.error("Error fetching audit details:", error);
      handleAlert("Error loading audit details", "error");
    }
  };

  const handleAlert = (message: string, type: "success" | "error" | "info") => {
    const alertObj: SingleAlertInfo = {
      message,
      alertType: type,
    };
    dispatch(setSingleAlertObj(alertObj));
  };

  const handleBackClick = () => {
    navigate(-1);
    // navigate("/ai_medical_audit", { state: { withSearchHistory: true } });
  };

  const handleCloseMedicalAuditPopup = () => {
    setShowMedicalAuditPopup(false);
    setSelectedMedicalAuditData(null);
  };

const handleViewMedicalAudit = async (aiAuditDetailId: string) => {
    try {
      const response = await dispatch(getAiAuditDetailByIdData(aiAuditDetailId));
      if (response.payload.code === SUCCESS_CODE && response.payload?.data) {
        const auditData = response.payload.data.responseJson || response.payload.data;
        
        // Handle failed audit status by showing the error from DB using the comment popup
        if (auditData?.status === "failed") {
          setCommentData({
            title: "Audit Failed",
            body: auditData.error || "Failed to process the audit. Please check the data and try again.",
          });
          setShowCommentPopup(true);
          return;
        }

        setSelectedMedicalAuditData(response.payload.data);
        const newAuditId = auditData?.audit_id || auditData?.auditId;
        if (newAuditId) {
          setAuditId(newAuditId);
        }
        setShowMedicalAuditPopup(true);
      } else {
        handleAlert("Failed to load audit details. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error fetching medical audit details:", error);
      handleAlert("Error loading audit details. Please try again.", "error");
    }
  };

  // Table columns for audit details
  const auditDetailColumns: Column[] = [
    { id: "createdByUserName", label: "Uploaded By", minWidth: 150 },
    { id: "compliancePercentage", label: "Compliance %", minWidth: 120 },
    { id: "createdAt", label: "Uploaded Date", minWidth: 150 },
    { id: "actionType", label: "Action Type", minWidth: 120 },
    { id: "action", label: "Action", minWidth: 100 },
  ];

  // Format audit detail rows
  const formattedAuditDetailRows = useMemo(() => {
    if (!allAuditDetails?.auditDetails || !Array.isArray(allAuditDetails.auditDetails)) return [];

    return allAuditDetails.auditDetails.map((detail: any) => {
      const complianceScore = detail.compliancePercentage || 0;

      return {
        createdByUserName: detail.createdByUserName || "-",
        compliancePercentage: (
          <Typography
            sx={{
              color: getComplianceColor(complianceScore),
              fontWeight: 600,
            }}
          >
            {complianceScore}%
          </Typography>
        ) as any,
        createdAt: detail.createdAt || "-",
        actionType: detail.actionType || "-",
        action: (
          <CustomIconButton
            color="success"
            size="small"
            variant="outlined"
            tooltipMessage="View Audit"
            tooltipPlacement="top"
            onClick={() => handleViewMedicalAudit(detail.aiAuditDetailId)}
          >
            <VisibilityOutlined fontSize="small" />
          </CustomIconButton>
        ),
      };
    });
  }, [allAuditDetails?.auditDetails]);

  return (
    <>
      <Box m="15px">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Header title="AI Medical Audit Details" />
          </Grid>
        </Grid>

        <Box
          sx={{
            border: 1,
            p: 2,
            borderRadius: 3,
            borderColor: colors.grey[700],
            mt: 2,
          }}
        >
          <Grid container spacing={3}>
            {/* Patient Info Card */}
            <Grid item md={4} xs={12}>
              <Card sx={{ padding: 0 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: colors.success[300], ml: 2, pt: 2 }}>
                  Patient Info
                </Typography>
                <Grid container spacing={2} p={2}>
                  <Grid item md={12} xs={12}>
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography color={colors.grey[500]}>Patient Name</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography>{allAuditDetails?.patientName || "-"}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color={colors.grey[500]}>Practice Name</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography>{allAuditDetails?.practiceName || "-"}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Audit Info Card */}
            <Grid item md={4} xs={12}>
              <Card sx={{ padding: 0 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: colors.success[300], ml: 2, pt: 2 }}>
                  Audit Info
                </Typography>
                <Grid container spacing={2} p={2}>
                  <Grid item md={12} xs={12}>
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography color={colors.grey[500]}>File Name</Typography>
                        </Grid>
                        <Grid item xs={8} sx={{ minWidth: 0 }}>
                          {/* CS-1706 - Prevent long PDF names from overflowing outside the audit info card. */}
                          <Typography
                            sx={{
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {allAuditDetails?.fileName || "-"}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color={colors.grey[500]}>Upload Date</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography>{allAuditDetails?.createdAt || "-"}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color={colors.grey[500]}>Uploaded By</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography>{allAuditDetails?.createdByUserName || "-"}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Compliance Info Card */}
            <Grid item md={4} xs={12}>
              <Card sx={{ padding: 0 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: colors.success[300], ml: 2, pt: 2 }}>
                  Compliance Info
                </Typography>
                <Grid container spacing={2} p={2}>
                  <Grid item md={12} xs={12}>
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography color={colors.grey[500]}>Overall Compliance</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="h6"
                            fontWeight="600"
                            sx={{
                              color: getComplianceColor(allAuditDetails?.currentCompliancePercentage || 0),
                            }}
                          >
                            {allAuditDetails?.currentCompliancePercentage || 0}%
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Audit Details Table */}
        {allAuditDetails && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item md={12} xs={12}>
                <Card sx={{ padding: 2 }}>
                  <Typography variant="h5" fontWeight="bold" mb={1} sx={{ color: colors.success[300] }}>
                    Audit History
                  </Typography>
                  <Box
                    sx={{
                      padding: "0px 10px 10px 10px",
                      border: `1px solid ${colors.grey[700]}`,
                      borderRadius: 2,
                      mt: 2,
                      p: 2,
                      maxHeight: "500px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <CustomTable
                      columns={auditDetailColumns}
                      rows={formattedAuditDetailRows}
                      emptyText="No audit details available"
                      showMenu={false}
                      currentPageKey="aiAuditDetails"
                      disablePagination={true}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box display="flex" justifyContent="end" mt="20px" pb={5}>
                 <Button
                   sx={{ mr: 1 }}
                   color="info"
                   startIcon={<ArrowBack />}
                   onClick={handleBackClick}
                 >
                   Back
                 </Button>
               </Box>
      </Box>
      {/* Medical Note Audit Popup */}
      <MedicalNoteAuditPopup
        open={showMedicalAuditPopup}
        onClose={handleCloseMedicalAuditPopup}
        initialAuditData={selectedMedicalAuditData}
        auditCompleted={true}
        onAuditComplete={(docId, auditData) => {
          const newAuditId = auditData?.audit_id || auditData?.auditId;
          if (newAuditId) setAuditId(newAuditId);
        }}
      />

      <CustomCommentPopup
        open={showCommentPopup}
        onClose={() => setShowCommentPopup(false)}
        title={commentData.title}
        commentBody={commentData.body}
      />
    </>
  );
};

export default AiAuditDetails;
